import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	SiteFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	ExperimentsSymbol,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import type { Experiments, ILogger, ISessionManager } from '@wix/thunderbolt-symbols'
import { SessionManagerSymbol } from 'feature-session-manager'
import type { FunctionBindingConfig, FunctionBindingItem } from '@wix/thunderbolt-becky-types'

import type {
	BuilderContextProvidersSiteConfig,
	BuilderContextProvidersPageConfig,
	ContextProviderLoader,
	ContextProviderRegistry,
	FunctionLibraryRegistry,
	IBuilderModuleLoader,
} from './types'
import {
	ContextProviderRegistrySymbol,
	FunctionLibraryRegistrySymbol,
	BuilderModuleLoaderSymbol,
	name,
} from './symbols'

export const BuilderContextProvidersLoader = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(PageFeatureConfigSymbol, name),
		BuilderModuleLoaderSymbol,
		ContextProviderRegistrySymbol,
		FunctionLibraryRegistrySymbol,
		LoggerSymbol,
		ExperimentsSymbol,
		SessionManagerSymbol,
	] as const,
	(
		siteFeatureConfig: BuilderContextProvidersSiteConfig,
		pageFeatureConfig: BuilderContextProvidersPageConfig,
		moduleLoader: IBuilderModuleLoader,
		contextProviderRegistry: ContextProviderRegistry,
		functionLibraryRegistry: FunctionLibraryRegistry,
		logger: ILogger,
		experiments: Experiments,
		sessionManager: ISessionManager
	): ContextProviderLoader => {
		const pendingContexts: { [contextType: string]: Promise<void> } = {}
		const pendingFunctionLibraries: { [type: string]: Promise<void> } = {}

		const retrieveFunctionLibrary = async (functionLibraryType: string): Promise<void> => {
			if (functionLibraryRegistry.has(functionLibraryType)) {
				return
			}

			try {
				const funcLibData = siteFeatureConfig.functionLibraries?.[functionLibraryType]
				if (!funcLibData) {
					throw new Error(`Function library "${functionLibraryType}" not found in site config`)
				}

				const funcLibModule: any = await moduleLoader.loadModule(funcLibData.componentUrl)
				functionLibraryRegistry.registerFunctionLibrary(functionLibraryType, funcLibModule)
			} catch (error) {
				logger.captureError(error, {
					tags: { feature: 'builder-context-providers', phase: 'loading-function-library' },
					extra: { functionLibraryType },
				})
			}
		}

		const loadFunctionLibrariesForComponent = async (compId: string): Promise<void> => {
			const bindings = pageFeatureConfig.compIdToBindingMap[compId] || []
			// Each FunctionBindingItem carries its own library type, so nested arguments must be preloaded too.
			const collectFunctionLibraryTypes = (
				functionBindingItem: FunctionBindingItem,
				functionLibraryTypes: Set<string>
			) => {
				functionLibraryTypes.add(functionBindingItem.functionLibraryType)
				const args = functionBindingItem.arguments || []
				args.forEach((arg) => {
					if (arg.type === 'FunctionBindingItem') {
						collectFunctionLibraryTypes(arg, functionLibraryTypes)
					}
				})
			}
			const functionLibraryTypes = new Set<string>()
			bindings
				.filter((b): b is FunctionBindingConfig => b.type === 'FunctionBindingConfig')
				.forEach((b) => collectFunctionLibraryTypes(b.functionBindingItem, functionLibraryTypes))

			await Promise.all(
				[...functionLibraryTypes].map((type) => {
					pendingFunctionLibraries[type] =
						pendingFunctionLibraries[type] ||
						retrieveFunctionLibrary(type).finally(() => {
							delete pendingFunctionLibraries[type]
						})
					return pendingFunctionLibraries[type]
				})
			)
		}

		return {
			retrieveContextProviders: async (compId: string) => {
				if (!experiments['specs.thunderbolt.contextProviders']) {
					return
				}

				const retrieveContextProvider = async (contextType: string): Promise<void> => {
					if (contextProviderRegistry.has(contextType)) {
						return
					}

					try {
						const contextProviderData = siteFeatureConfig.contextProviders[contextType]
						if (!contextProviderData) {
							throw new Error(`Context type "${contextType}" not found in site config`)
						}
						const urlToLoad =
							process.env.PACKAGE_NAME === 'thunderbolt-ds'
								? contextProviderData.editorComponentUrl || contextProviderData.componentUrl
								: contextProviderData.componentUrl

						const contextProviderModule: any = await moduleLoader.loadModule(urlToLoad)

						if (typeof contextProviderModule.injectAccessTokenGetter === 'function') {
							const tokenGetter = sessionManager.getBoundAccessTokenFunction(contextProviderData.appDefinitionId)
							contextProviderModule.injectAccessTokenGetter(tokenGetter)
						}

						if (!contextProviderModule.default) {
							throw new Error(`Module for "${contextType}" is missing default export (Provider component)`)
						}
						contextProviderRegistry.registerContextProvider(contextType, contextProviderModule)
					} catch (error) {
						logger.captureError(error, {
							tags: { feature: 'builder-context-providers', phase: 'loading' },
							extra: { contextType, compId },
						})
					}
				}

				const contexts = pageFeatureConfig.compIdToContextsMap[compId] || []
				await Promise.all([
					...contexts.map(async (context) => {
						const { contextType } = context
						pendingContexts[contextType] =
							pendingContexts[contextType] ||
							retrieveContextProvider(contextType).finally(() => {
								delete pendingContexts[contextType]
							})
						return pendingContexts[contextType]
					}),
					loadFunctionLibrariesForComponent(compId),
				])
			},
		}
	}
)
